import { useEffect } from 'react';
import { logout } from '../api/auth';
import { trackVersionEvent } from '../telemetry/clientTelemetry';

interface VersionInfo {
  version: string;
  buildTime: string;
}

const VERSION_STORAGE_KEY = 'appVersion';
const KEYS_TO_PRESERVE = ['themeMode', 'bolt_terms_accepted']; // Preserve user preferences
const PREFIX_TO_PRESERVE = ['profileImage_', 'profileImageTimestamp_']; // Preserve cached profile images

/**
 * Hook to check if the frontend version has changed since the last load.
 * If a new version is detected, it clears the client state and logs out the user.
 */
export const useVersionCheck = () => {
  useEffect(() => {
    const checkVersion = async () => {
      try {
        // Fetch current deployed version with cache-busting
        const response = await fetch(`/version.json?_=${Date.now()}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok) {
          console.warn('Failed to fetch version.json, skipping version check');
          return;
        }

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.toLowerCase().includes('application/json')) {
          console.warn('version.json did not return JSON, skipping version check');
          return;
        }

        const versionInfo: VersionInfo = await response.json();
        if (!versionInfo?.version) {
          console.warn('version.json payload missing version field, skipping version check');
          return;
        }
        const newVersion = versionInfo.version;
        
        // Get stored version from localStorage
        const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
        
        if (storedVersion && storedVersion !== newVersion) {
          // New version detected!
          console.debug(`Version changed: ${storedVersion} → ${newVersion}. Logging out...`);
          trackVersionEvent('changed', storedVersion, newVersion);
          
          // Clear app state (preserve theme, profile images, and other user preferences)
          const preservedData: Record<string, string> = {};
          
          // Preserve specific keys
          KEYS_TO_PRESERVE.forEach(key => {
            const value = localStorage.getItem(key);
            if (value !== null) {
              preservedData[key] = value;
            }
          });
          
          // Preserve keys with specific prefixes (like profile images)
          Object.keys(localStorage).forEach(key => {
            PREFIX_TO_PRESERVE.forEach(prefix => {
              if (key.startsWith(prefix)) {
                const value = localStorage.getItem(key);
                if (value !== null) {
                  preservedData[key] = value;
                }
              }
            });
          });
          
          // Clear all localStorage
          localStorage.clear();
          
          // Restore preserved data
          Object.entries(preservedData).forEach(([key, value]) => {
            localStorage.setItem(key, value);
          });
          
          // Update to new version
          localStorage.setItem(VERSION_STORAGE_KEY, newVersion);
          
          // Clear sessionStorage
          sessionStorage.clear();
          
          // Trigger logout
          try {
            await logout();
          } catch (error) {
            console.error('Error during logout:', error);
          }
          
          // Force page reload to login page
          window.location.reload();
        } else if (!storedVersion) {
          // First load, just store the version
          console.debug(`Storing initial version: ${newVersion}`);
          trackVersionEvent('initial', null, newVersion);
          localStorage.setItem(VERSION_STORAGE_KEY, newVersion);
        } else {
          // Same version, no action needed
          console.debug(`Version check passed: ${newVersion}`);
          trackVersionEvent('unchanged', storedVersion, newVersion);
        }
      } catch (error) {
        console.warn('Failed to check version, skipping:', error);
        // Fail silently - don't block the app
      }
    };

    checkVersion();
  }, []);
};
